import React from 'react';
import { Stack, Typography, Pagination, Box, useTheme, useMediaQuery } from '@mui/material';
import ApprovalCard from './ApprovalCard';
import ApprovalCardSkeleton from './ApprovalCardSkeleton';
import { ApprovalData } from '../types';

interface IndigentListProps {
    data: ApprovalData[];
    page: number;
    totalItems: number;
    itemsPerPage: number;
    onPageChange: (page: number) => void;
    isLoading?: boolean;
    onStatusUpdate?: (
        requestId: string,
        status: 'reviewed' | 'failed' | 'approved' | 'rejected',
        remark?: string,
        file?: File,
    ) => Promise<void>;
    updatingStatus?: string | null;
}

const IndigentList: React.FC<IndigentListProps> = ({
    data,
    page,
    totalItems,
    itemsPerPage,
    onPageChange,
    isLoading = false,
    onStatusUpdate,
    updatingStatus,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

    if (isLoading) {
        return (
            <Stack spacing={{ xs: 2, sm: 2.5, md: 3 }} mb={{ xs: 3, sm: 3.5, md: 4 }}>
                {Array.from({ length: 5 }).map((_, index) => (
                    <ApprovalCardSkeleton key={index} />
                ))}
            </Stack>
        );
    }

    if (data.length === 0) {
        return (
            <Box display='flex' justifyContent='center' alignItems='center' minHeight='200px'>
                <Typography 
                    variant='h6' 
                    color='text.secondary'
                    sx={{ 
                        fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
                        textAlign: 'center',
                        px: { xs: 2, sm: 0 }
                    }}
                >
                    No approvals found
                </Typography>
            </Box>
        );
    }

    return (
        <>
            <Stack spacing={{ xs: 2, sm: 2.5, md: 3 }} mb={{ xs: 3, sm: 3.5, md: 4 }}>
                {data.map(item => (
                    <ApprovalCard
                        key={item.id}
                        data={item}
                        onStatusUpdate={onStatusUpdate}
                        isUpdating={updatingStatus === item.id}
                    />
                ))}
            </Stack>

            <Stack 
                direction={isMobile ? 'column' : 'row'} 
                justifyContent={isMobile ? 'center' : 'flex-end'} 
                alignItems='center' 
                spacing={isMobile ? 2 : (isTablet ? 2.5 : 3)}
                sx={{ 
                    mt: { xs: 2, sm: 3, md: 4 },
                    px: { xs: 1, sm: 0 }
                }}
            >
                <Typography 
                    fontSize={{ xs: 14, sm: 15, md: 16, lg: 17 }} 
                    sx={{ 
                        fontFamily: 'Poppins, sans-serif',
                        textAlign: isMobile ? 'center' : 'left',
                        order: isMobile ? 2 : 1
                    }}
                >
                    Showing {(page - 1) * itemsPerPage + 1} to{' '}
                    {Math.min(page * itemsPerPage, totalItems)} of {totalItems} results
                </Typography>
                <Pagination
                    count={Math.ceil(totalItems / itemsPerPage)}
                    page={page}
                    onChange={(_, value) => onPageChange(value)}
                    size={isMobile ? 'small' : (isTablet ? 'medium' : 'large')}
                    sx={{
                        order: isMobile ? 1 : 2,
                        '& .MuiPaginationItem-root': {
                            fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
                            minWidth: { xs: '32px', sm: '36px', md: '40px' },
                            height: { xs: '32px', sm: '36px', md: '40px' },
                        }
                    }}
                />
            </Stack>
        </>
    );
};

export default IndigentList;
